"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.contractEffortSchema = exports.effortBucketSchema = exports.effortBurnSchema = exports.effortBurnPointSchema = exports.effortPersonSchema = exports.effortTaskSchema = exports.effortLineItemSchema = exports.effortPeriodSchema = void 0;
const eac_1 = require("./eac");
const contract_1 = require("./contract");
const zod_1 = require("zod");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C65 · the project Dashboard tab — earned value in HOURS.                  │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE SAME QUESTIONS AS THE FINANCE TAB, ASKED OF TIME.** *Is the frame going to last* and
 * *are we ahead or behind* are the two, and a delivery lead asks them in hours long before anybody
 * asks them in forints: a retainer's whole conversation is `42:00 a month` against `37:00 logged`,
 * with no money in it at all. `finance.ts` answers against budgets and rate cards; this answers
 * against `estimate_hours` and the period's share of it.
 *
 * ⚠️ **Which is why it is a second read rather than a flag on the first.** Every figure here has a
 * money counterpart that is NOT a conversion of it — a person with no rate card costs nothing and
 * still consumes the frame — so one read serving both would have to carry two of everything and a
 * rule for which is authoritative. Two reads, one occurrence generator, one workday kernel.
 *
 * ⚠️⚠️ **THE WIRE CARRIES PRIMITIVES; THE RATIOS ARE DERIVED ON THE CLIENT.** `RATE`, `RATE_OK`,
 * `ΔRATE`, the burn-out date and every row of `SOROZAT` are arithmetic over the fields below, and
 * `finance-model.ts` is the precedent: the panels read one model so a tile cannot disagree with the
 * bar beneath it. Sending the ratios too would be sending the same fact twice.
 */
const wireDateSchema = zod_1.z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** ⚠️ Hours are strings on the wire like every other `numeric` — `H:MM` is a rendering. */
const hours = zod_1.z.string();
/**
 * ⚠️⚠️ **THE FRAME IS THE RETAINER'S ESTIMATE, AND BILLABLE WORK IS BESIDE IT RATHER THAN IN IT.**
 * The mockup's own note: *"A kereten felüli (elszámolható) munka nem növeli és nem fogyasztja a
 * keretet."* A support contract's over-frame development is real hours somebody worked and real
 * hours somebody bills, and counting them against the monthly frame would make every busy month
 * read as an overrun.
 */
exports.effortPeriodSchema = zod_1.z.object({
    index: zod_1.z.number().int(),
    startsOn: wireDateSchema,
    endsOn: wireDateSchema,
    state: zod_1.z.enum(['past', 'current', 'future']),
    /** The period's frame — Σ the stored per-period estimates (P2-11) of the retainers in it. */
    budget: hours,
    /** Σ allocation across the period's days. */
    planned: hours,
    /** Σ worklogs on the retainer line items. */
    logged: hours,
    /** Σ worklogs on the `billable` line items — over the frame, and never inside it. */
    billable: hours,
    /** Planned overtime inside the period, already multiplied. */
    overtime: hours,
    /** Working days in the period, and how many have elapsed. ⚠️ The workspace's calendar, not Mon–Fri. */
    workdays: zod_1.z.number().int(),
    workdaysDone: zod_1.z.number().int(),
});
/** One deliverable inside the scope — the `SORTÉTELEK AZ IDŐSZAKBAN` table. */
exports.effortLineItemSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** R-57 · the address the screen links to. */
    identifier: zod_1.z.string(),
    number: zod_1.z.number().int(),
    name: zod_1.z.string(),
    /* ⚠️⚠️ **`LINE_ITEM_TYPES`, NOT A COPY OF ITS MEMBERS.** Three schemas each inlined the
       same three strings, so C55's `license` typechecked in `contract.ts` and silently
       disagreed here — caught by the compiler only because the API assigns between them. */
    type: zod_1.z.enum(contract_1.LINE_ITEM_TYPES),
    scope: zod_1.z.enum(['retainer', 'billable']),
    /**
     * ⚠️ U-4 · the contract the deliverable is on — the SZERZŐDÉS column of the project-wide view,
     * and where a click on the row goes. On a contract read, that contract.
     */
    contractId: zod_1.z.string(),
    contractIdentifier: zod_1.z.string(),
    contractName: zod_1.z.string(),
    /**
     * The SCOPE's share of the frame. ⚠️ P2-11: the estimate is stored per period, so inside a period
     * this is that stored figure (zero where the deliverable does not run), and across the whole
     * contract it is that × the periods the deliverable spans. `null` on a billable item: it has no
     * share of the frame to be measured against.
     */
    budget: hours.nullable(),
    planned: hours,
    logged: hours,
    /** What the deliverable is estimated at in total (per period × its span), whatever the window. */
    estimate: hours,
    /** Σ task estimate × the status weight — the same `EARNED_WEIGHT` the money tab earns by. */
    earned: hours,
    /**
     * The deliverable's own window. ⚠️ `null` at either end means *the contract's* — an open-ended
     * deliverable runs to the contract's last day, and the table says «nyitott» rather than guessing
     * a date the agreement does not contain.
     */
    startsOn: wireDateSchema.nullable(),
    endsOn: wireDateSchema.nullable(),
    /**
     * ⚠️ Completion in TASKS, beside completion in hours. `3/8` and `62%` answer different questions —
     * one deliverable can be most of the way through its hours with every task still open.
     */
    taskCount: zod_1.z.number().int(),
    doneTasks: zod_1.z.number().int(),
    /**
     * ⚠️ The worst slip among this deliverable's overdue, still-open tasks, in WORKING days. `null`
     * where nothing is overdue — a zero would read as *on time to the day*, which is a claim.
     */
    slipWorkdays: zod_1.z.number().int().nullable(),
});
/**
 * One task, with the figures the mockup's `FELADATOK` table draws.
 *
 * ⚠️ **A task's CPI is hours over hours**, so it is the one place in the product where the index
 * means exactly what its name says without a rate card in the way.
 */
exports.effortTaskSchema = zod_1.z.object({
    id: zod_1.z.string(),
    key: zod_1.z.string(),
    title: zod_1.z.string(),
    assigneeName: zod_1.z.string().nullable(),
    statusName: zod_1.z.string(),
    statusCategory: zod_1.z.enum(['open', 'in_progress', 'in_review', 'closed']),
    lineItemName: zod_1.z.string().nullable(),
    /** ⚠️ U-4 · `null` for a task with no line item — its hours still count on the project view. */
    lineItemId: zod_1.z.string().nullable(),
    contractIdentifier: zod_1.z.string().nullable(),
    contractName: zod_1.z.string().nullable(),
    estimate: hours,
    logged: hours,
    /** The status weight, so the client can show the ladder rather than re-deriving it. */
    weight: zod_1.z.string(),
    earned: hours,
    dueOn: wireDateSchema.nullable(),
    /** ⚠️ What `NYITOTT FELADAT KORA` ages from. A task with no start has no age, rather than age 0. */
    startedOn: wireDateSchema.nullable(),
});
/** One person's hours inside the scope — the `ERŐFORRÁS` panel and its donut. */
exports.effortPersonSchema = zod_1.z.object({
    memberId: zod_1.z.string(),
    name: zod_1.z.string(),
    /**
     * ⚠️⚠️ **THEIR PROJECT ROLE — `project_members.project_role_id`, never the permission role.**
     * `null` for somebody with no membership overlapping the scope, or one with no role: the panel's
     * *Nincs projektszerepkör* group.
     */
    roleName: zod_1.z.string().nullable(),
    /** The project role's own colour, `#RRGGBB`. */
    roleColor: zod_1.z.string().nullable(),
    /**
     * ⚠️ **C68 · what this person's generated face is drawn from.** The normalised email,
     * produced by `wireAvatarSeed`. This shape carries no avatar URL, which is why the first
     * pass missed it — it went looking for pictures and this row only ever had initials.
     */
    avatarSeed: zod_1.z.string().nullable(),
    /**
     * ⚠️ **Their seniority band — round 5 item 10.** The people table groups by person, seniority
     * or role, and only the first of the three needed no new field. Null for a member who has none
     * set, which groups under *nincs megadva* rather than vanishing.
     */
    seniority: zod_1.z.string().nullable(),
    planned: hours,
    logged: hours,
    overtime: hours,
    /** Their capacity across the scope, so the panel can say what share of it this contract took. */
    capacity: hours,
});
/**
 * One column of the chart. ⚠️ Bucketed server-side, like the finance chart's.
 *
 * ⚠️⚠️ **THE COLUMNS ARE PERIODS OR CALENDAR BUCKETS, AND THE SWITCH IS NOT THE GRANULARITY
 * CONTROL.** A retainer looked at WHOLE draws one column per period — `#1 … #9`, each against its
 * own frame — because that is the unit the contract is written in. Narrow it to one period and
 * there is only one of those, so the chart falls back to days, weeks or months like any other
 * scope. The mockup's `periodMode = isRec && !selOcc`.
 */
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ 30 Oct 2026 · BURN-DOWN AND BURN-UP, IN HOURS — EVM's three lines.         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️ **ADDED UNDER RULE 8.** One point per chart column (the same columns as `buckets`), each
 * carrying both charts' lines:
 *
 * - **`tasks`** — a deliverable's work. *Scope* is BAC: the tasks' estimates as they stood that day
 *   (a closed task with no estimate counts the hours it took — `earnedHoursOf`). *Done* is EV: the
 *   same tasks earned, weighted by status. *Plan* is PV: the allocation, cumulative.
 * - **`frame`** — a retainer's picked periods: *scope* is their frame, *done* the hours that
 *   consumed it, *plan* the allocation inside it.
 *
 * The burn-down reads `remaining` (scope − done), `plannedRemaining` (today's scope − plan), the
 * straight `ideal` from today's scope to zero over the working days, and `forecast` — what is left
 * at the pace work has actually been done so far, from today on. A future column has no `done` and
 * no `remaining`; a past one has no `forecast`.
 */
exports.effortBurnPointSchema = zod_1.z.object({
    key: zod_1.z.string(),
    label: zod_1.z.string(),
    /** The column's last day — what each figure is "as of". */
    to: wireDateSchema,
    future: zod_1.z.boolean(),
    scope: hours,
    done: hours.nullable(),
    plan: hours,
    remaining: hours.nullable(),
    plannedRemaining: hours,
    ideal: hours,
    forecast: hours.nullable(),
});
exports.effortBurnSchema = zod_1.z.object({
    kind: zod_1.z.enum(['tasks', 'frame']),
    /**
     * The first day the audit log describes these tasks — before it, a task's state is the earliest
     * one recorded (imported work has no history). `null` with nothing recorded, or for a frame.
     */
    historyFrom: wireDateSchema.nullable(),
    points: zod_1.z.array(exports.effortBurnPointSchema),
});
exports.effortBucketSchema = zod_1.z.object({
    key: zod_1.z.string(),
    label: zod_1.z.string(),
    from: wireDateSchema,
    to: wireDateSchema,
    future: zod_1.z.boolean(),
    /** ⚠️ Whether TODAY falls inside this column — what the marker and the bold label hang off. */
    current: zod_1.z.boolean(),
    planned: hours,
    logged: hours,
    overtime: hours,
    /**
     * ⚠️ The frame this column is measured against, and `null` where there ISN'T one: a calendar
     * week carries no share of a retainer's frame, and drawing it a cap would invent one.
     */
    budget: hours.nullable(),
    /**
     * ⚠️⚠️ **WHERE THIS COLUMN LANDS AT THE CURRENT PACE, AND `null` WHERE THAT IS NOT A QUESTION.**
     * A closed bucket has an answer rather than a forecast, and the mockup draws a dash. The rate is
     * counted in WORKING days, which is why this is computed here and not beside the bar.
     */
    projected: hours.nullable(),
});
exports.contractEffortSchema = zod_1.z.object({
    contractId: zod_1.z.string(),
    contractCode: zod_1.z.string(),
    /** R-57 · the contract's public identifier, its address. */
    contractIdentifier: zod_1.z.string(),
    contractName: zod_1.z.string(),
    /**
     * ⚠️⚠️ U-4 · **`true` is "Teljes projekt" — every contract of a project at once**
     * (`GET /projects/:id/effort`). The four `contract*` fields above then name the PROJECT (its key
     * prefix as the code), `recurring` is false (two contracts' periods do not align, so the view is
     * the `plain` variant with a calendar chart) and every line item and task says which contract it
     * is on.
     */
    wholeProject: zod_1.z.boolean(),
    recurring: zod_1.z.boolean(),
    /** The scope's window — the whole contract, or one period. */
    from: wireDateSchema,
    to: wireDateSchema,
    today: wireDateSchema,
    /** The period being looked at, or `null` for the whole contract. */
    selectedPeriod: zod_1.z.number().int().nullable(),
    /**
     * ⚠️ **The frame for the SCOPE** — one period's share when a period is selected, the whole
     * retainer estimate otherwise. The two sub-labels the tile needs travel with it.
     */
    budget: hours,
    budgetTotal: hours,
    periodCount: zod_1.z.number().int(),
    /** ⚠️ Whether the contract has any over-frame work at all; the tile is absent when it has none. */
    hasBillable: zod_1.z.boolean(),
    planned: hours,
    /**
     * ⚠️⚠️ **PV — WHAT THE PLAN SAID SHOULD BE DONE BY TODAY**, and the denominator of SPI. `planned`
     * is the whole window's allocation; this is the part of it that has already come due. Dividing
     * the earned value by the whole window's plan would make every contract look behind on day one.
     */
    plannedToDate: hours,
    logged: hours,
    billable: hours,
    overtime: hours,
    workdays: zod_1.z.number().int(),
    workdaysDone: zod_1.z.number().int(),
    /**
     * ⚠️⚠️ **THE DAY THE FRAME RUNS OUT AT THE CURRENT RATE, AND THE ONE FIGURE THE CLIENT
     * CANNOT WORK OUT.** *Keret kifutása* counts forward in WORKING days — the workspace's own, with
     * its holidays and its relocated Saturdays — and the browser has no calendar to count with.
     * Everything else on this screen is arithmetic over the numbers beside it; this is a date.
     *
     * ⚠️ `null` when nothing is being consumed, when the frame is already spent, or when the answer
     * falls outside the scope — *“not until after this ends”* is not a date somebody should be shown
     * as one.
     */
    burnOutOn: wireDateSchema.nullable(),
    /**
     * ⚠️⚠️ **WHEN THE REMAINING WORK RUNS OUT AGAINST THE PLAN** — *Előrejelzett befejezés*, and the
     * ÜTEM panel's anchor. Walked day by day through the allocation rather than divided by an
     * average, because a plan that puts four people on next week and nobody after finishes on a day
     * no average produces. `null` when there is nothing left to do, or nothing planned to do it with.
     */
    forecastFinishOn: wireDateSchema.nullable(),
    /** *Csúszás* in working days against `to`: positive is late. ⚠️ `null` with no forecast finish. */
    slipWorkdays: zod_1.z.number().int().nullable(),
    /** Σ estimate and Σ earned across the scope's deliverables — the A-variant's schedule figures. */
    estimate: hours,
    earned: hours,
    periods: zod_1.z.array(exports.effortPeriodSchema),
    lineItems: zod_1.z.array(exports.effortLineItemSchema),
    tasks: zod_1.z.array(exports.effortTaskSchema),
    people: zod_1.z.array(exports.effortPersonSchema),
    buckets: zod_1.z.array(exports.effortBucketSchema),
    /** 30 Oct 2026 · the burn-down and burn-up charts — `effortBurnSchema`. `null` where none apply. */
    burn: exports.effortBurnSchema.nullable(),
    /** 30 Oct 2026 · EV · the five EACs in hours, each with its finish (rule 8). */
    eac: eac_1.eacEvaluationSchema.nullable(),
});
//# sourceMappingURL=effort.js.map